import React, { useState, useEffect } from "react";
import {
  LayoutDashboard,
  MapPin,
  Users,
  Bell,
  Clock,
  ArrowRight,
  Target,
  Rocket,
  Trophy,
  Star,
  Zap,
  Play,
  Lock,
  Building2,
  User,
  BookOpen,
  Crown,
  Flame,
  ChevronRight,
  MoreHorizontal,
  Search,
  Check,
  ShieldCheck,
  BarChart3,
  Calendar,
} from "lucide-react";
import logoMarca from "@/assets/logoMarca.png";

type Screen = "dashboard" | "temples" | "analytics";

interface MockupScreenProps {
  active: boolean;
  children: React.ReactNode;
}

const MockupScreen = ({ active, children }: MockupScreenProps) => (
  <div
    className={`absolute inset-0 transition-opacity duration-700 ease-in-out bg-[#0f0f0f] ${
      active ? "opacity-100 z-10" : "opacity-0 pointer-events-none z-0"
    }`}
  >
    {children}
  </div>
);

// Sidebar Component
const Sidebar = ({ activeItem }: { activeItem: string }) => {
  const menuItems = [
    { id: "dashboard", icon: LayoutDashboard },
    { id: "temples", icon: MapPin },
    { id: "analytics", icon: BarChart3 },
    { id: "mentors", icon: Users },
    { id: "profile", icon: User },
  ];

  return (
    <div className="w-[60px] flex-shrink-0 bg-[#1a1a1a] border-r border-[#2a2a2a] flex flex-col py-6 items-center z-20 relative h-full">
      {/* Logo */}
      <div className="mb-8">
        <div className="w-9 h-9 bg-brand-accent rounded-lg flex items-center justify-center shadow-lg shadow-brand-accent/20">
          <img src={logoMarca} alt="ES" className="w-6 h-6 rounded" />
        </div>
      </div>

      {/* Menu Items */}
      <div className="space-y-6 flex-1 w-full px-3 flex flex-col items-center">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeItem === item.id;
          return (
            <div
              key={item.id}
              className={`group flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-300 cursor-default relative ${
                isActive
                  ? "text-brand-accent bg-brand-accent/10"
                  : "text-gray-500 hover:text-gray-300"
              }`}
            >
              <Icon
                className={`w-5 h-5 transition-transform duration-300 ${isActive ? "scale-100" : "group-hover:scale-110"}`}
              />
              {isActive && (
                <div className="absolute -left-3 w-1 h-4 bg-brand-accent rounded-r-full" />
              )}
            </div>
          );
        })}
      </div>

      {/* User Avatar */}
      <div className="mt-auto pt-6 border-t border-[#2a2a2a] w-full flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-brand-accent to-brand-primary-light p-[1px]">
          <div className="w-full h-full rounded-full bg-[#1a1a1a] flex items-center justify-center">
            <span className="text-[10px] font-bold text-white">GV</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Screen
const DashboardScreen = () => (
  <div className="flex h-full bg-[#0f0f0f] font-sans text-white">
    <Sidebar activeItem="dashboard" />

    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Top Bar */}
      <div className="h-14 border-b border-[#2a2a2a] flex items-center justify-between px-6 bg-[#0f0f0f]/50 backdrop-blur-md sticky top-0 z-10">
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-widest">
            Painel de Controle
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-[#1a1a1a] px-3 py-1 rounded-full border border-[#2a2a2a]">
            <Zap className="w-3 h-3 text-brand-accent fill-brand-accent" />
            <span className="text-xs font-bold text-white">Lvl. 5</span>
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 overflow-hidden min-w-0">
        <div className="flex gap-6 h-full">
          {/* Main Content Area - Left */}
          <div className="flex-[2] flex flex-col gap-6 min-w-0">
            {/* Welcome Card */}
            <div className="bg-gradient-to-r from-brand-primary-dark to-[#1a1a1a] border border-[#2a2a2a] p-5 rounded-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-32 bg-brand-accent/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-brand-accent/10 transition-colors duration-500"></div>

              <div className="relative z-10">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h1 className="text-xl font-bold text-white whitespace-nowrap">
                      Olá, Estrategista
                    </h1>
                    <p className="text-xs text-gray-400">
                      Sua métrica de autoridade subiu 15%.
                    </p>
                  </div>
                  <div className="bg-white/10 p-2 rounded-lg">
                    <Target className="w-4 h-4 text-brand-accent" />
                  </div>
                </div>

                <div className="mt-3">
                  <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                    <span>Progresso do Templo Atual</span>
                    <span>75%</span>
                  </div>
                  <div className="h-1.5 w-full bg-[#2a2a2a] rounded-full overflow-hidden">
                    <div className="h-full bg-brand-accent w-3/4 shadow-[0_0_10px_rgba(201,162,39,0.5)]"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Row */}
            <div className="flex gap-4">
              <div className="flex-1 bg-[#1a1a1a] p-3 rounded-xl border border-[#2a2a2a] hover:border-brand-accent/20 transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  <div className="p-1 bg-green-500/10 rounded-md">
                    <BarChart3 className="w-3 h-3 text-green-500" />
                  </div>
                  <span className="text-[10px] text-gray-400 font-medium">
                    Conversão
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-bold text-white">4.8%</span>
                  <span className="text-[10px] text-green-500">+1.2%</span>
                </div>
              </div>
              <div className="flex-1 bg-[#1a1a1a] p-3 rounded-xl border border-[#2a2a2a] hover:border-brand-accent/20 transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  <div className="p-1 bg-blue-500/10 rounded-md">
                    <Users className="w-3 h-3 text-blue-500" />
                  </div>
                  <span className="text-[10px] text-gray-400 font-medium">
                    Leads
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-bold text-white">1,240</span>
                  <span className="text-[10px] text-blue-500">+124</span>
                </div>
              </div>
            </div>
          </div>

          {/* Side Content Area - Right */}
          <div className="flex-1 flex flex-col gap-4 min-w-0">
            <div className="bg-[#1a1a1a] p-4 rounded-xl border border-[#2a2a2a] flex-1">
              <h3 className="text-xs font-semibold text-white mb-3 tracking-wide">
                PRÓXIMAS TAREFAS
              </h3>
              <div className="space-y-2">
                {[
                  { title: "Definir Big Idea", status: "wip", time: "30m" },
                  { title: "Review de Copy", status: "todo", time: "1h" },
                  { title: "Análise de Avatar", status: "done", time: "45m" },
                ].map((task, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 p-1.5 hover:bg-[#252525] rounded-lg transition-colors cursor-pointer group"
                  >
                    <div
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${task.status === "wip" ? "bg-brand-accent animate-pulse" : task.status === "done" ? "bg-green-500" : "bg-gray-600"}`}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-[10px] font-medium truncate ${task.status === "done" ? "text-gray-500 line-through" : "text-gray-200"}`}
                      >
                        {task.title}
                      </p>
                    </div>
                    <div className="text-[9px] text-gray-500 border border-[#333] px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {task.time}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Temples Journey Screen
const TemplesScreen = () => {
  return (
    <div className="flex h-full bg-[#0f0f0f] font-sans text-white">
      <Sidebar activeItem="temples" />
      <div className="flex-1 flex flex-col h-full">
        <div className="h-14 border-b border-[#2a2a2a] flex items-center px-6 bg-[#0f0f0f]/50 backdrop-blur-md">
          <h2 className="text-sm font-bold text-white">Mapa da Jornada</h2>
        </div>

        <div className="flex-1 p-8 overflow-hidden relative">
          <div className="absolute top-0 left-8 bottom-0 w-[2px] bg-[#2a2a2a]"></div>

          <div className="space-y-8 relative">
            {[
              { name: "Fundamentos", status: "active", icon: Building2 },
              { name: "Identidade", status: "locked", icon: ShieldCheck },
              { name: "Narrativa", status: "locked", icon: BookOpen },
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-6 relative">
                <div
                  className={`w-8 h-8 rounded-full border-2 flex items-center justify-center z-10 bg-[#0f0f0f] ${step.status === "active" ? "border-brand-accent text-brand-accent shadow-[0_0_15px_rgba(201,162,39,0.3)]" : "border-[#333] text-gray-600"}`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${step.status === "active" ? "bg-brand-accent" : "bg-transparent"}`}
                  ></div>
                </div>

                <div
                  className={`flex-1 p-4 rounded-xl border ${step.status === "active" ? "bg-[#1a1a1a] border-brand-accent/30" : "bg-[#151515] border-[#222] opacity-60"} transition-all`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-bold text-white">
                      {step.name}
                    </h3>
                    {step.status === "locked" && (
                      <Lock className="w-3 h-3 text-gray-600" />
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    Módulo de estruturação básica.
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export function SystemMockup() {
  const [currentScreen, setCurrentScreen] = useState<Screen>("dashboard");
  const screens: Screen[] = ["dashboard", "temples"];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentScreen((prev) => {
        const currentIndex = screens.indexOf(prev);
        return screens[(currentIndex + 1) % screens.length];
      });
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full aspect-[16/10] bg-[#0a0a0a] relative overflow-hidden group select-none">
      {/* Screen Content */}
      <div className="absolute inset-0 bg-[#0f0f0f] overflow-hidden">
        <MockupScreen active={currentScreen === "dashboard"}>
          <DashboardScreen />
        </MockupScreen>
        <MockupScreen active={currentScreen === "temples"}>
          <TemplesScreen />
        </MockupScreen>
      </div>

      {/* Overlays for realism */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-white/5 to-transparent opacity-20"></div>
      <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/10 rounded-xl"></div>
    </div>
  );
}
